// In-run boost items (Reveal, Shield, Freeze).

import {
    getInventoryItemCount,
    grantInventoryItem,
} from "./inventory";

export const BOOST_ITEM_IDS = {
    REVEAL: "boost_reveal",
    SECOND_CHANCE: "boost_second_chance",
    FREEZE: "boost_freeze",
};

export const BOOST_CATALOG = [
    {
        id: BOOST_ITEM_IDS.REVEAL,
        runKey: "reveal",
        name: "Reveal",
        shortLabel: "Reveal",
        description: "Highlight one card you have not picked yet.",
        icon: "👁",
        iconSrc: `${process.env.PUBLIC_URL || ""}/images/boosts/boost-reveal.png`,
    },
    {
        id: BOOST_ITEM_IDS.SECOND_CHANCE,
        runKey: "second_chance",
        name: "Shield",
        shortLabel: "Shield",
        description: "Your next mistake does not cost a heart.",
        icon: "🛡",
        iconSrc: `${process.env.PUBLIC_URL || ""}/images/boosts/boost-shield.png`,
    },
    {
        id: BOOST_ITEM_IDS.FREEZE,
        runKey: "freeze",
        name: "Freeze",
        shortLabel: "Freeze",
        description: "Your next correct pick will not shuffle the board.",
        icon: "❄",
        iconSrc: `${process.env.PUBLIC_URL || ""}/images/boosts/boost-freeze.png`,
    },
];

export const ADMIN_BOOST_GRANT_EACH = 5;

/** Treats prices for individual boosts and the full kit. */
export const BOOST_TREAT_COSTS = {
    [BOOST_ITEM_IDS.REVEAL]: 750,
    [BOOST_ITEM_IDS.SECOND_CHANCE]: 1000,
    [BOOST_ITEM_IDS.FREEZE]: 1000,
};

export const BOOST_BUNDLE_TREAT_COST = 2500;
export const BOOST_BUNDLE_SHOP_ID = "treats_shop_boost_bundle";

export function getBoostCatalogEntryByItemId(itemId = "") {
    return (
        BOOST_CATALOG.find((entry) => entry.id === String(itemId || "")) ||
        null
    );
}

export function clampBoostBundleCount(count = 0) {
    return Math.max(0, Math.min(25, Math.floor(Number(count) || 0)));
}

export function formatBoostBundleLabel(bundleCount = 0) {
    const count = clampBoostBundleCount(bundleCount);
    if (count <= 0) {
        return "";
    }
    return count === 1
        ? "Boost Bundle (Reveal + Shield + Freeze)"
        : `${count} Boost Bundles`;
}

export function createEmptyRunBoostState() {
    return {
        runBoostRevealHighlightId: null,
        runBoostSecondChanceArmed: false,
        runBoostFreezeArmed: false,
        runBoostUsed: {
            reveal: false,
            second_chance: false,
            freeze: false,
        },
        boostMessage: "",
    };
}

export function createEmptyRunBoostUsage() {
    return {
        reveal: false,
        second_chance: false,
        freeze: false,
    };
}

export function grantAllBoostItems(inventory = null, amount = ADMIN_BOOST_GRANT_EACH) {
    return BOOST_CATALOG.reduce(
        (nextInventory, boost) =>
            grantInventoryItem(nextInventory, boost.id, amount),
        inventory
    );
}

export function grantBoostBundle(inventory = null, amount = 1) {
    return grantAllBoostItems(inventory, clampBoostBundleCount(amount));
}

function overlaysBlockBoosts(state = {}) {
    return Boolean(
        state.showStoryIntro ||
            state.showGuideHeartDemo ||
            state.showShinyDiscovery ||
            state.showHeartContinueOverlay ||
            state.showHeartEmptyOverlay ||
            state.showResumeOverlay ||
            state.showLevelCompleteOverlay ||
            state.showExitConfirm
    );
}

export function getBoostActivationBlockReason({
    boost = null,
    state = {},
    isGuidedRun = false,
    unusedCardCount = 0,
    inventory = null,
} = {}) {
    if (!boost) {
        return "Unavailable";
    }

    if (state.screen !== "game") {
        return "Only during a run";
    }

    if (!state.runStartedAt || state.runPausedAt) {
        return "Run not active";
    }

    if (isGuidedRun) {
        return "Not during the tutorial";
    }

    if (state.hardcoreMode || state.runMode === "blitz") {
        return "Not in Hardcore or Blitz";
    }

    if (overlaysBlockBoosts(state)) {
        return "Finish the current prompt first";
    }

    if (getInventoryItemCount(inventory, boost.id) <= 0) {
        return "None in inventory";
    }

    if (boost.runKey === "reveal" && unusedCardCount <= 0) {
        return "Board already cleared";
    }

    if (boost.runKey === "reveal" && state.runBoostRevealHighlightId) {
        return "Tap the glowing card first";
    }

    return null;
}

export function getArmedBoostStatusMessage(state = {}) {
    const shieldArmed = Boolean(state.runBoostSecondChanceArmed);
    const freezeArmed = Boolean(state.runBoostFreezeArmed);

    if (shieldArmed && freezeArmed) {
        return "Shield active · Freeze active";
    }

    if (shieldArmed) {
        return "Shield active";
    }

    if (freezeArmed) {
        return "Freeze active";
    }

    return "";
}

export function pickRevealTargetId(unusedIds = [], shyHiddenCardIds = []) {
    const unused = (unusedIds || []).map((id) => String(id));
    if (!unused.length) {
        return null;
    }

    const hidden = new Set((shyHiddenCardIds || []).map((id) => String(id)));
    const visible = unused.filter((id) => !hidden.has(id));
    const pool = visible.length ? visible : unused;
    return pool[Math.floor(Math.random() * pool.length)] || null;
}

export function listBoostBarEntries({
    state = {},
    inventory = null,
    isGuidedRun = false,
    unusedCardCount = 0,
} = {}) {
    return BOOST_CATALOG.map((boost) => {
        const count = getInventoryItemCount(inventory, boost.id);
        const blockReason = getBoostActivationBlockReason({
            boost,
            state,
            isGuidedRun,
            unusedCardCount,
            inventory,
        });
        const armed =
            boost.runKey === "second_chance"
                ? Boolean(state.runBoostSecondChanceArmed)
                : boost.runKey === "freeze"
                  ? Boolean(state.runBoostFreezeArmed)
                  : Boolean(state.runBoostRevealHighlightId);

        return {
            ...boost,
            count,
            armed,
            disabled: Boolean(blockReason),
            disabledReason: blockReason || "",
        };
    });
}
